import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { ModalWrapComponent } from '@revspringinc/rs-shared';
import { ModalService } from '@revspringinc/rs-shared';
import {
    ProcedureService,
    ProcedureCategoryModel,
    ProcedureGroupModel,
    OrganizationLocation,
    SiteConfigService,
} from '@shopping';
import { catchError, EMPTY, lastValueFrom, Subscription, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { RsModalConfig } from '@revspringinc/rs-shared';
import { ShoppingActivityType } from '../../main/shopping-activity-type';
import { LocationDeterminationService } from '../../services/location-determination.service';
import { ShoppingSessionService } from '../../main/session.service';
import { BaseComponent } from '@pa-ui';

@Component({
    selector: 'rs-select-services',
    templateUrl: './select-services.component.html',
    styleUrls: ['./select-services.component.scss'],
    standalone: false
})
export class SelectServicesModal extends BaseComponent implements ModalWrapComponent, OnInit, OnDestroy {
    public static modalName = 'SelectServicesModal';
    @ViewChild('modal', { static: true }) public modal;

    public modalConfig = {
        activityType: ShoppingActivityType.SelectServices,
        name: 'SelectServicesModal',
        title: 'Select Services',
        maxWidth: '650px',
        maxHeight: '70vh',
        bodyClass: 'gray-bck px-0',
        blockBackdropScrolling: true,
        footer: {
            hidden: false,
            save: 'Save',
            saveEnabled: false,
        },
    } as RsModalConfig;

    public modalName?: string;
    public visible?: boolean;
    public searchMode: boolean = false;
    public payloadSub?: any;
    public proceduresList: ProcedureCategoryModel[] = [];
    public orgLayoutConfig: any;

    public options: any;
    public expandedCategoryIndex: number | null = null;
    public expandedProcedureGroups: ProcedureGroupModel[] | null = null;
    public checkboxFormArray: UntypedFormArray | null = null;
    private proceduresList$: Subscription;
    private expandedCategorySubscription?: Subscription;

    constructor(
        private readonly modalService: ModalService,
        private readonly procedureService: ProcedureService,
        private readonly sessionService: ShoppingSessionService,
        private readonly translate: TranslateService,
        private readonly locationDeterminationService: LocationDeterminationService,
        private readonly siteConfig: SiteConfigService,
    ) {
        super();
    }

    public async ngOnInit(): Promise<void> {}

    public ngOnDestroy(): void {
        this.proceduresList$?.unsubscribe();
        this.expandedCategorySubscription?.unsubscribe();
    }

    public show(options: {
        selectedProcedures: ProcedureGroupModel[];
        locationId: number;
        locations: OrganizationLocation[];
        hasLocationEnabled: boolean;
    }): void {
        this.options = options;

        this.proceduresList$ = this.procedureService
            .procedureApiControllerGetProcedureCategories(this.sessionService.sessionId, this.options.locationId)
            .subscribe((procedureList) => {
                this.proceduresList = procedureList.config;
                this.setLanguage(procedureList.language);

                this.selectedProceduresChanged(this.options.selectedProcedures);
            });

        this.siteConfig
            .siteConfigApiControllerGetSiteConfig(this.sessionService.sessionId, 'org-layout')
            .pipe(
                takeUntil(this.subscriptionNotifier),
                catchError(() => {
                    return EMPTY;
                }),
            )
            .subscribe((config) => {
                this.orgLayoutConfig = config;
            });
    }

    public cancel(target?: any) {
        this.hide();
    }

    public hide(inState?: boolean): void {
        this.modal.hide();
    }

    public async submit() {
        if (!this.options.selectedProcedures || this.options.selectedProcedures.length === 0) {
            this.modalService.hide();
        } else if (this.options.hasLocationEnabled) {
            const { activeList, inactiveList } = this.locationDeterminationService.determineLocationLimitations(
                this.options,
            );

            if (inactiveList.length === this.options.locations.length || activeList.length === 0) {
                await lastValueFrom(
                    this.modalService.show('NoticeMessageModal', 'ProcedureLocationConflictNoticeMessage'),
                );
            } else if (activeList.length === 1) {
                const location = this.options.locations.find((x) => x.id === activeList[0].id);
                this.modalService.hide({ procedures: this.options.selectedProcedures, location });
            } else {
                this.modalService.hide({ procedures: this.options.selectedProcedures });
            }
        } else {
            const unsupportedProcedures = this.options.selectedProcedures
                .flatMap((group) => group.procedures ?? [])
                .filter((procedure) => !(procedure as any).hasChargeMaster);

            if (unsupportedProcedures.length) {
                await lastValueFrom(
                    this.modalService.show('NoticeMessageModal', 'ProcedureHasNoChargeMasterNoticeMessage'),
                );
            }

            this.modalService.hide({ procedures: this.options.selectedProcedures });
        }
    }

    public selectedProceduresChanged(selectedProcedureGroups: ProcedureGroupModel[]) {
        this.options.selectedProcedures = [];
        this.setSelectedProcedureGroups(selectedProcedureGroups);
    }

    public toggleCategory(procedureCategory: ProcedureCategoryModel, index: number): void {
        if (this.expandedCategoryIndex === index) {
            this.expandedCategoryIndex = null;
            this.expandedProcedureGroups = null;
            this.checkboxFormArray = null;
            return;
        }

        this.expandedCategoryIndex = index;
        this.expandedProcedureGroups = null;
        this.checkboxFormArray = null;
        this.expandedCategorySubscription?.unsubscribe();

        this.expandedCategorySubscription = this.procedureService
            .procedureApiControllerGetCategoryById(
                this.sessionService.sessionId,
                procedureCategory.id,
                this.options.locationId,
            )
            .subscribe((procedureGroups) => {
                if (this.options.hasLocationEnabled && this.options.locationId && procedureGroups?.config?.groups) {
                    procedureGroups.config.groups = procedureGroups.config.groups.filter((x) => {
                        const locations: OrganizationLocation[] = Array.isArray((x as any).locations) ? (x as any).locations : [];
                        const locationMatch = locations.some(
                            (location: OrganizationLocation) => location.id === this.options.locationId,
                        );
                        return !locations.length || locationMatch;
                    });
                }

                procedureGroups?.config?.groups?.forEach((x) => {
                    (x as any).selected = this.options.selectedProcedures.some((p) => p.id === x.id);
                });

                this.expandedProcedureGroups = procedureGroups?.config?.groups?.map((procedureGroup) =>
                    procedureGroup.proceduresAreVisible
                        ? { ...procedureGroup, procedures: procedureGroup.procedures }
                        : { ...procedureGroup, procedures: procedureGroup.procedures?.length ? [procedureGroup.procedures[0]] : [] },
                ) ?? [];

                this.checkboxFormArray = this.createCheckboxes();
                this.setLanguage(procedureGroups.language);
            });
    }

    public selectProcedureGroup(selected: boolean, procedureGroup: ProcedureGroupModel): void {
        procedureGroup.selected = selected;

        if (selected) {
            if (!this.options.selectedProcedures.some((p) => p.id === procedureGroup.id)) {
                this.options.selectedProcedures.push(procedureGroup);
            }
        } else {
            this.options.selectedProcedures = this.options.selectedProcedures.filter(
                (p) => p.id !== procedureGroup.id,
            );
        }

        this.reevaluateSelectedProcedures();
        this.modalConfig.footer.saveEnabled = this.options.selectedProcedures.length > 0;
    }

    public createCheckboxes(): UntypedFormArray {
        const controls = this.expandedProcedureGroups!.map(
            (procedureGroup) => new UntypedFormControl(procedureGroup.selected),
        );
        return new UntypedFormArray(controls);
    }

    public removeProcedure(procedureGroup: ProcedureGroupModel): void {
        this.options.selectedProcedures = this.options.selectedProcedures.filter(
            (p) => p.id !== procedureGroup.id,
        );

        const expandedIndex = this.expandedProcedureGroups?.findIndex((p) => p.id === procedureGroup.id) ?? -1;
        if (expandedIndex !== -1) {
            this.expandedProcedureGroups![expandedIndex].selected = false;
            this.checkboxFormArray?.at(expandedIndex)?.setValue(false, { emitEvent: false });
        }

        this.reevaluateSelectedProcedures();
        this.modalConfig.footer.saveEnabled = this.options.selectedProcedures.length > 0;
    }

    public showContactMeModal(searchTerm?: string) {
        const currentProcedures = this.options.selectedProcedures?.map((x) => this.translate.instant(x.name)) ?? [];
        this.modalService.show('ContactMeModal', { currentProcedures, searchTerm });
    }

    private setSelectedProcedureGroups(selectedProcedureGroups: ProcedureGroupModel[], shouldAdd: boolean = true) {
        this.modalConfig.footer.saveEnabled =
            selectedProcedureGroups.length > 0 || this.options.selectedProcedures.length > 0;

        if (shouldAdd && selectedProcedureGroups && selectedProcedureGroups.length > 0) {
            this.options.selectedProcedures.push(...selectedProcedureGroups);
        }

        this.reevaluateSelectedProcedures();
    }

    private reevaluateSelectedProcedures() {
        this.proceduresList?.forEach((procedureGroup) => {
            (procedureGroup as any).selected = this.options.selectedProcedures?.filter(
                (spg) => spg.categoryId === procedureGroup.id,
            );
        });
    }

    private setLanguage(language: any) {
        Object.keys(language).forEach((lang) => this.translate.setTranslation(lang, language[lang], true));
    }
}
